import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL as __BACKEND_URL__ } from "../lib/backend-url";

const API = `${__BACKEND_URL__}/api`;

/**
 * Hook for stock management with v2 API support.
 * Uses optimized endpoints with caching.
 */
export function useStock() {
  const [stockProducts, setStockProducts] = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [expiringProducts, setExpiringProducts] = useState([]);
  const [stockProductDetail, setStockProductDetail] = useState(null);
  const [loadingStockDetail, setLoadingStockDetail] = useState(false);
  const [deletingMovement, setDeletingMovement] = useState(null);

  // Use v2 API for products (with caching), fallback to legacy
  const loadStockProducts = useCallback(async (storeId = "", useCache = true) => {
    setLoadingStock(true);
    try {
      const params = new URLSearchParams();
      if (storeId) params.append('store_id', storeId);
      if (!useCache) params.append('no_cache', 'true');
      
      // Try v2 endpoint first, fallback to legacy
      let url = `${API}/stock/v2/products?${params}`;
      let response = await fetch(url);
      
      // Fallback to legacy API if v2 fails
      if (!response.ok) {
        console.warn('V2 API failed, falling back to legacy');
        url = `${API}/stock/products?${params}`;
        response = await fetch(url);
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success) {
        setStockProducts(data.products || []);
      }
    } catch (error) {
      console.error('Error loading stock:', error);
      toast.error('Erro ao carregar estoque');
    } finally {
      setLoadingStock(false);
    }
  }, []);

  const loadLowStockProducts = useCallback(async (storeId = "") => {
    try {
      const params = new URLSearchParams();
      if (storeId) params.append('store_id', storeId);
      const response = await axios.get(`${API}/stock/low-stock?${params}`);
      if (response.data.success) {
        setLowStockProducts(response.data.products || []);
      }
    } catch (error) {
      console.error('Error loading low stock:', error);
    }
  }, []);

  const loadExpiringProducts = useCallback(async (storeId = "", days = 5, minCount = 10) => {
    try {
      const params = new URLSearchParams();
      if (storeId) params.append('store_id', storeId);
      params.append('days', days);
      params.append('min_count', minCount);
      const response = await axios.get(`${API}/stock/expiring?${params}`);
      if (response.data.success) {
        setExpiringProducts(response.data.products || []);
      }
    } catch (error) {
      console.error('Error loading expiring products:', error);
    }
  }, []);

  // Use v2 API for product detail (with caching), fallback to legacy
  const loadStockProductDetail = useCallback(async (ean, storeId = "") => {
    setLoadingStockDetail(true);
    try {
      const params = new URLSearchParams();
      if (storeId) params.append('store_id', storeId);
      
      // Try v2 endpoint first
      let url = `${API}/stock/v2/products/${ean}?${params}`;
      let response;
      try {
        response = await axios.get(url);
      } catch (e) {
        // Fallback to legacy API if v2 fails
        console.warn('V2 API failed, falling back to legacy');
        url = `${API}/stock/products/${ean}?${params}`;
        response = await axios.get(url);
      }
      
      if (response.data.success) {
        // Normalize response: V2 returns data inside 'product', V1 returns at root level
        const data = response.data;
        if (data.product && !data.batches) {
          // V2 format: extract from product, but keep root level flags
          const batches = data.product.batches || [];
          const hasNfeEntry = batches.some(b => b.nfe_id || b.nfe_number);
          setStockProductDetail({
            ...data,
            product: data.product,
            batches: batches,
            movements: data.product.movements || [],
            has_nfe_entry: hasNfeEntry
          });
        } else {
          // V1 format: use as-is
          setStockProductDetail(data);
        }
      }
    } catch (error) {
      console.error('Error loading stock detail:', error);
      toast.error('Erro ao carregar detalhes');
    } finally {
      setLoadingStockDetail(false);
    }
  }, []);

  const updateMinStock = useCallback(async (ean, minStock) => {
    try {
      const response = await axios.put(`${API}/stock/products/${ean}`, { min_stock: parseInt(minStock) });
      if (response.data.success) {
        toast.success('Estoque mínimo atualizado');
        // Invalidate cache after update
        await axios.post(`${API}/stock/cache/invalidate/products`);
        return true;
      }
    } catch (error) {
      console.error('Error updating min stock:', error);
      toast.error('Erro ao atualizar estoque mínimo');
    }
    return false;
  }, []);

  const updateProductImage = useCallback(async (ean, imageBase64) => {
    try {
      const response = await axios.put(`${API}/stock/products/${ean}`, { image_base64: imageBase64 });
      if (response.data.success) {
        toast.success('Imagem atualizada com sucesso!');
        return true;
      }
    } catch (error) {
      console.error('Error updating product image:', error);
      toast.error('Erro ao atualizar imagem');
    }
    return false;
  }, []);

  const updateBatchExpiry = useCallback(async (batchId, expiryDate) => {
    try {
      const params = new URLSearchParams();
      if (expiryDate) params.append('expiry_date', expiryDate);
      const response = await axios.put(`${API}/stock/batches/${batchId}?${params}`);
      if (response.data.success) {
        toast.success('Validade do lote atualizada!');
        return true;
      }
    } catch (error) {
      console.error('Error updating batch expiry:', error);
      toast.error('Erro ao atualizar validade');
    }
    return false;
  }, []);

  const processSalesForStock = useCallback(async () => {
    try {
      toast.info('Processando vendas para baixa no estoque...');
      const response = await axios.post(`${API}/stock/process-sales`);
      if (response.data.success) {
        toast.success(`Vendas processadas! ${response.data.deductions_count || 0} baixas realizadas.`);
        // Invalidate cache after processing
        await axios.post(`${API}/stock/cache/invalidate/products`);
        return true;
      }
    } catch (error) {
      console.error('Error processing sales:', error);
      toast.error('Erro ao processar vendas');
    }
    return false;
  }, []);

  const deleteMovement = useCallback(async (movementId) => {
    setDeletingMovement(movementId);
    try {
      const response = await axios.delete(`${API}/stock/movements/${movementId}`);
      if (response.data.success) {
        toast.success('Movimentação excluída e estoque ajustado!');
        // Invalidate cache after deletion
        await axios.post(`${API}/stock/cache/invalidate/products`);
        return response.data;
      }
    } catch (error) {
      console.error('Error deleting movement:', error);
      toast.error('Erro ao excluir movimentação');
    } finally {
      setDeletingMovement(null);
    }
    return null;
  }, []);

  const clearStockProductDetail = useCallback(() => {
    setStockProductDetail(null);
  }, []);

  // Utility to invalidate stock cache
  const invalidateCache = useCallback(async () => {
    try {
      await axios.post(`${API}/stock/cache/invalidate/products`);
    } catch (error) {
      console.error('Error invalidating cache:', error);
    }
  }, []);

  return {
    stockProducts,
    loadingStock,
    lowStockProducts,
    stockProductDetail,
    loadingStockDetail,
    deletingMovement,
    loadStockProducts,
    loadLowStockProducts,
    loadExpiringProducts,
    expiringProducts,
    loadStockProductDetail,
    updateMinStock,
    updateProductImage,
    updateBatchExpiry,
    processSalesForStock,
    deleteMovement,
    clearStockProductDetail,
    invalidateCache
  };
}
